import React, { useState } from 'react';

/**
 * AmbassadorBadge
 * ===============
 * Gold hexagon checkmark for "Founding Member: Premium Ambassador".
 * Shown privately next to the user's name in their dashboard / profile settings only.
 *
 * Props:
 *   size      {number}  — diameter in px (default 20)
 *   glow      {boolean} — enable pulsing glow animation (Referral Portal = true)
 *   tooltip   {boolean} — show hover tooltip (default true)
 *   className {string}
 */

/* ── Hex SVG Polygon ────────────────────────────────────────────────────────
 * A perfect hexagon pointing upward, inset 10% for visual weight.
 */
const HEX_POINTS = '50,5 93,27.5 93,72.5 50,95 7,72.5 7,27.5';

const AmbassadorBadge = ({
  size      = 20,
  glow      = false,
  tooltip   = true,
  className = '',
}) => {
  const [showTip, setShowTip] = useState(false);

  return (
    <span
      className={`relative inline-flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
      onMouseEnter={() => tooltip && setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      {/* ── Glow ring (Referral Portal only) ── */}
      {glow && (
        <span
          className="absolute inset-0 rounded-full animate-ping opacity-30"
          style={{
            background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)',
            animationDuration: '2.4s',
          }}
        />
      )}

      {/* ── Hexagon SVG ── */}
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className="relative z-10"
        aria-label="Premium Ambassador"
      >
        {/* Gradient definitions */}
        <defs>
          <linearGradient id="amb-gold-fill" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#FDE68A" />
            <stop offset="50%"  stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#B45309" />
          </linearGradient>
          <linearGradient id="amb-gold-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#FEF3C7" />
            <stop offset="100%" stopColor="#D97706" />
          </linearGradient>
          {glow && (
            <filter id="amb-glow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          )}
        </defs>

        {/* Hexagon fill */}
        <polygon
          points={HEX_POINTS}
          fill="url(#amb-gold-fill)"
          stroke="url(#amb-gold-stroke)"
          strokeWidth="3"
          filter={glow ? 'url(#amb-glow)' : undefined}
        />

        {/* Checkmark — drawn as a thick path centred in the hex */}
        <polyline
          points="32,50 44,63 68,37"
          fill="none"
          stroke="#1C1917"
          strokeWidth="9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {/* ── Tooltip ── */}
      {tooltip && showTip && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50
                     bg-[#0D1323] border border-amber-500/40 rounded-xl px-3 py-2.5
                     shadow-2xl shadow-amber-500/10 pointer-events-none whitespace-nowrap"
          style={{ minWidth: 220 }}
        >
          {/* Arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 overflow-hidden">
            <div className="absolute inset-0 bg-[#0D1323] border-b border-r border-amber-500/40 rotate-45 translate-y-[-50%]" />
          </div>

          <div className="flex items-start gap-2">
            {/* Mini hex */}
            <svg viewBox="0 0 100 100" width={16} height={16} className="flex-shrink-0 mt-0.5">
              <polygon points={HEX_POINTS} fill="#F59E0B" stroke="#FDE68A" strokeWidth="4" />
              <polyline points="32,50 44,63 68,37" fill="none" stroke="#1C1917" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <p className="text-[11px] font-black text-amber-300 leading-tight mb-0.5">
                Founding Member: Premium Ambassador
              </p>
              <p className="text-[10px] text-amber-400/70 leading-snug">
                You are one of the first 1,000 users to build Ariv.
              </p>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default AmbassadorBadge;
